/**
 * Store Service (Framework Agnostic)
 * 
 * This service provides a clean abstraction over store operations.
 * It uses the IStore interface, making it easy to swap implementations
 * (e.g., from Redux to Zustand, Jotai, or any other state management library)
 */

import type {
  IStoreConfig,
  IStoreWithPersistence,
  IPersistConfig,
} from './types';
import { createReduxStore } from './adapters/reduxAdapter';

/**
 * Store Service Factory
 * Creates and manages store instances
 */
export class StoreService {
  private _store: IStoreWithPersistence | null = null;

  /**
   * Create a store instance
   * 
   * @param config - Store configuration
   * @returns Store instance with optional persistence support
   */
  createStore<State = unknown>(
    config: IStoreConfig<State> & { persistConfig?: IPersistConfig },
  ): IStoreWithPersistence<State> {
    // Use Redux adapter by default
    // In the future, this could be swapped for other adapters
    const store = createReduxStore(config);
    this._store = store as IStoreWithPersistence;
    return store;
  }

  /**
   * Get the current store instance
   */
  get store(): IStoreWithPersistence | null {
    return this._store;
  }

  /**
   * Set the store instance (for advanced use cases)
   */
  set store(store: IStoreWithPersistence | null) {
    this._store = store;
  }
}

/**
 * Singleton instance of StoreService
 * Use this to create and access the store throughout the application
 */
export const storeService = new StoreService();
